import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConnectionStore } from '@/stores/connectionStore';
import {
  Settings,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Pause,
  MessageSquare,
} from 'lucide-react';

type ExtensionStatus = 'active' | 'paused' | 'needs_attention';

interface Extension {
  id: string;
  name: string;
  description: string;
  status: ExtensionStatus;
  createdAt: string;
  lastUsed?: string;
  usageCount: number;
}

const MOCK_EXTENSIONS: Extension[] = [
  {
    id: '1',
    name: 'Slack alerts for call quality issues',
    description: 'Posts to #it-alerts when MOS drops below 3.5 for more than 5 minutes',
    status: 'active',
    createdAt: 'Jan 15',
    lastUsed: '2 hours ago',
    usageCount: 47,
  },
  {
    id: '2',
    name: 'New hire onboarding workflow',
    description: 'When a user is created, assigns to Support queue, enables AI Companion, sends welcome email via Outlook',
    status: 'active',
    createdAt: 'Jan 20',
    lastUsed: 'Yesterday',
    usageCount: 23,
  },
  {
    id: '3',
    name: 'Weekly license optimization report',
    description: 'Every Monday, analyzes usage and emails recommendations to admin@company.com',
    status: 'active',
    createdAt: 'Feb 1',
    lastUsed: '5 days ago',
    usageCount: 6,
  },
  {
    id: '4',
    name: 'ServiceNow ticket on queue overflow',
    description: 'Creates incident ticket when any queue wait time exceeds 15 minutes',
    status: 'needs_attention',
    createdAt: 'Feb 3',
    lastUsed: '3 days ago',
    usageCount: 12,
  },
  {
    id: '5',
    name: 'Monthly adoption summary for leadership',
    description: 'Generates executive summary of feature adoption trends, sends to leadership DL',
    status: 'paused',
    createdAt: 'Jan 25',
    lastUsed: 'Jan 31',
    usageCount: 1,
  },
];

export function ExtendPage() {
  const navigate = useNavigate();
  const { isConnected } = useConnectionStore();
  const [isLoading, setIsLoading] = useState(true);
  const [extensions, setExtensions] = useState<Extension[]>([]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      setExtensions(MOCK_EXTENSIONS);
      setIsLoading(false);
    };

    if (isConnected) {
      load();
    } else {
      setIsLoading(false);
    }
  }, [isConnected]);

  const activeCount = extensions.filter(e => e.status === 'active').length;
  const needsAttentionCount = extensions.filter(e => e.status === 'needs_attention').length;

  if (!isConnected) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <main className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            <Settings className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">Connect Your Zoom Account</h2>
            <p className="mt-2 text-muted-foreground">
              Connect your Zoom account to create automations.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Connect in Chat
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-3xl p-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-medium text-foreground">Automate</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Automations Zac has built for you. Describe new ones in Chat.
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : extensions.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 text-foreground font-medium">No automations yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Describe what you need in the chat and Zac will build it.
              </p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="flex gap-4 mb-4 text-sm">
                <span className="text-muted-foreground">
                  <span className="text-foreground font-medium">{activeCount}</span> active
                </span>
                {needsAttentionCount > 0 && (
                  <span className="text-yellow-400">
                    {needsAttentionCount} needs attention
                  </span>
                )}
              </div>

              {/* Extensions list */}
              <div className="space-y-2">
                {extensions.map(extension => (
                  <div
                    key={extension.id}
                    className={`border border-border rounded-lg p-4 hover:bg-secondary/30 transition-colors cursor-pointer ${
                      extension.status === 'paused' ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {extension.status === 'active' && (
                          <CheckCircle className="h-4 w-4 text-green-400" />
                        )}
                        {extension.status === 'paused' && (
                          <Pause className="h-4 w-4 text-muted-foreground" />
                        )}
                        {extension.status === 'needs_attention' && (
                          <AlertTriangle className="h-4 w-4 text-yellow-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground">{extension.name}</span>
                        <p className="text-xs text-muted-foreground mt-1">{extension.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>Created {extension.createdAt}</span>
                          <span>·</span>
                          <span>{extension.usageCount} uses</span>
                          {extension.lastUsed && (
                            <>
                              <span>·</span>
                              <span>Last used {extension.lastUsed}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
